import React, { useEffect, useState } from "react";
import {
    Alert, AlertIcon, Badge, Box, Button, Flex, FormControl, FormHelperText, FormLabel, Heading, Input,
    SimpleGrid, Skeleton, Switch, Text, useToast,
} from '@chakra-ui/react';
import { getParametrosFiscaisVenda, salvarParametrosFiscaisVenda } from "../services/api";

const VAZIO = {
    pis_cofins_cst: '', aliq_pis: '', aliq_cofins: '', pis_cofins_exclui_icms: false,
    ibscbs_cst: '', ibscbs_cclasstrib: '', aliq_ibs_uf: '', aliq_ibs_mun: '', aliq_cbs: '', ibscbs_exclui_icms: false,
};

const formatarData = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');

// Parametros fiscais da NF-e de venda que ficam para configurar com o contador.
// CRT 3 (Lucro Presumido): sem tudo preenchido, a venda em CRT 3 e recusada antes de emitir.
const ParametrosFiscaisVenda = () => {
    const toast = useToast();
    const [dados, setDados] = useState(null);
    const [form, setForm] = useState(VAZIO);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        let vivo = true;
        getParametrosFiscaisVenda().then((resposta) => {
            if (!vivo) return;
            setDados(resposta);
            if (resposta?.crt3) setForm({ ...VAZIO, ...resposta.crt3 });
            setCarregando(false);
        });
        return () => { vivo = false; };
    }, []);

    const campo = (nome) => ({
        value: form[nome] ?? '',
        onChange: (e) => setForm((f) => ({ ...f, [nome]: e.target.value })),
    });
    const chave = (nome) => ({
        isChecked: !!form[nome],
        onChange: (e) => setForm((f) => ({ ...f, [nome]: e.target.checked })),
    });

    const salvar = async () => {
        setSalvando(true);
        const resposta = await salvarParametrosFiscaisVenda(form);
        setSalvando(false);
        if (!resposta.ok) {
            toast({ title: 'Não foi possível salvar', description: resposta.mensagem, status: 'error', duration: 7000, isClosable: true });
            return;
        }
        setDados(resposta.data);
        setForm({ ...VAZIO, ...resposta.data.crt3 });
        toast({ title: 'Parâmetros salvos', status: 'success', duration: 3000 });
    };

    if (carregando) {
        return <Skeleton h="260px" rounded="lg" />;
    }

    if (!dados) {
        return (
            <Alert status="error" rounded="md">
                <AlertIcon />
                Não consegui carregar os parâmetros fiscais da venda. Recarregue a página.
            </Alert>
        );
    }

    return (
        <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
            <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={2} mb={1}>
                <Heading size="md">Venda em Lucro Presumido (CRT 3)</Heading>
                <Badge colorScheme={dados.crt3_completo ? 'green' : 'orange'} fontSize="11px">
                    {dados.crt3_completo ? 'PRONTO' : 'A CONFIGURAR'}
                </Badge>
            </Flex>
            <Text fontSize="sm" color="gray.600" mb={4}>
                A partir de {formatarData(dados.data_presumido)} (homologação: {formatarData(dados.data_presumido_hmg)}) a NF-e de venda sai em CRT 3,
                sem as frases do Simples. Os valores abaixo vêm do contador; enquanto faltar algum, a venda em CRT 3 é recusada antes de emitir.
                Até lá vale o CRT 2 (ICMS normal desde {formatarData(dados.data_icms_normal)}), com PIS/COFINS CST 49 zerado.
            </Text>

            <Text fontSize="sm" fontWeight="semibold" mb={2}>PIS / COFINS</Text>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={5}>
                <FormControl>
                    <FormLabel fontSize="xs" mb={1}>CST</FormLabel>
                    <Input size="sm" maxLength={2} inputMode="numeric" placeholder="ex.: 01" {...campo('pis_cofins_cst')} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Alíquota PIS (%)</FormLabel>
                    <Input size="sm" inputMode="decimal" placeholder="ex.: 0,65" {...campo('aliq_pis')} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Alíquota COFINS (%)</FormLabel>
                    <Input size="sm" inputMode="decimal" placeholder="ex.: 3,00" {...campo('aliq_cofins')} />
                </FormControl>
                <FormControl display="flex" flexDirection="column" justifyContent="flex-end">
                    <FormLabel fontSize="xs" mb={1}>Tirar o ICMS da base</FormLabel>
                    <Switch colorScheme="orange" {...chave('pis_cofins_exclui_icms')} />
                </FormControl>
            </SimpleGrid>

            <Text fontSize="sm" fontWeight="semibold" mb={2}>IBS / CBS</Text>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3}>
                <FormControl>
                    <FormLabel fontSize="xs" mb={1}>CST</FormLabel>
                    <Input size="sm" maxLength={3} inputMode="numeric" placeholder="3 dígitos" {...campo('ibscbs_cst')} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" mb={1}>cClassTrib</FormLabel>
                    <Input size="sm" maxLength={6} inputMode="numeric" placeholder="6 dígitos" {...campo('ibscbs_cclasstrib')} />
                </FormControl>
                <FormControl display="flex" flexDirection="column" justifyContent="flex-end">
                    <FormLabel fontSize="xs" mb={1}>Tirar o ICMS da base</FormLabel>
                    <Switch colorScheme="orange" {...chave('ibscbs_exclui_icms')} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Alíquota IBS estadual (%)</FormLabel>
                    <Input size="sm" inputMode="decimal" {...campo('aliq_ibs_uf')} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Alíquota IBS municipal (%)</FormLabel>
                    <Input size="sm" inputMode="decimal" {...campo('aliq_ibs_mun')} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Alíquota CBS (%)</FormLabel>
                    <Input size="sm" inputMode="decimal" {...campo('aliq_cbs')} />
                    <FormHelperText fontSize="10px">Deixe vazio o que ainda não foi definido.</FormHelperText>
                </FormControl>
            </SimpleGrid>

            <Flex justify="flex-end" mt={5}>
                <Button colorScheme="orange" onClick={salvar} isLoading={salvando} loadingText="Salvando..." w={{ base: '100%', sm: 'auto' }}>
                    Salvar
                </Button>
            </Flex>
        </Box>
    );
};

export default ParametrosFiscaisVenda;
